import { useEffect, useState } from "react";
import { Loader2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { api, API_BASE, getToken, fileUrl } from "@/lib/api";
import { fmtDateTime, fmtTemp } from "@/lib/format";

export function PlanillaDetail({ submissionId, open, onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!submissionId || !open) { setS(null); return; }
    api.get(`/planillas/submissions/${submissionId}`).then(({ data }) => setS(data)).catch(() => {});
  }, [submissionId, open]);

  const amap = s ? Object.fromEntries((s.answers || []).map((a) => [a.question_id, a])) : {};
  const ansBySection = {};
  (s?.answers || []).forEach((a) => { (ansBySection[a.section_id] ||= []).push(a); });
  const pdf = () => window.open(`${API_BASE}/planillas/submissions/${submissionId}/pdf?auth=${getToken()}`, "_blank");

  const renderRow = (sec, amapRow) => (
    <div className="divide-y divide-slate-100 rounded-xl border border-[#E2E8F0]">
      {sec.questions.map((q) => {
        if (q.type === "instruction") {
          const warn = q.style === "warning";
          return (
            <div key={q.id} className={`p-3 text-sm ${warn ? "bg-amber-50" : "bg-blue-50/50"}`}>
              {q.label && <p className={`font-semibold ${warn ? "text-amber-800" : "text-[#1D4ED8]"}`}>{q.label}</p>}
              {q.help_text && <p className={`mt-0.5 whitespace-pre-line text-xs ${warn ? "text-amber-700" : "text-[#1e3a8a]"}`}>{q.help_text}</p>}
            </div>
          );
        }
        const a = amapRow[q.id] || {};
        const nc = a.noncompliance;
        const evs = a.evidence || [];
        return (
          <div key={q.id} className="p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[#153D35]">{q.label}</span>
              <span className={`font-medium ${nc ? "text-red-600" : "text-[#153D35]"}`}>
                {q.type === "file" ? (evs.length ? "Adjunto" : "—") : (["number", "temperature"].includes(q.type) && a.value != null ? fmtTemp(a.value, q.unit) : (a.value ?? "—"))}
                {nc && " ⚠"}
              </span>
            </div>
            {a.observation && <p className="mt-1 text-xs text-amber-700">Obs: {a.observation}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              {evs.map((ev, i) => (ev.type || "").startsWith("image/")
                ? <img key={i} src={fileUrl(ev.path)} alt="" className="h-20 w-20 rounded-lg object-cover" />
                : <a key={i} href={fileUrl(ev.path)} target="_blank" rel="noreferrer" data-testid={`detail-file-${q.id}-${i}`} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-xs text-[#2563EB] hover:underline"><FileText className="h-4 w-4" /> {ev.name || "Documento"}</a>)}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Detalle de planilla</DialogTitle></DialogHeader>
        {!s ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div><p className="font-display text-lg font-semibold">{s.template_snapshot?.name}</p>
                <p className="text-xs text-[#68756F]">{s.location_name} · {s.area_name || "—"} · v{s.template_version}</p></div>
              <StatusPill status={s.status} size="lg" />
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F8FAFC] p-3 text-sm">
              <div><span className="text-slate-400">Empresa</span><p className="font-medium">{s.organization_name}</p></div>
              <div><span className="text-slate-400">Fecha</span><p className="font-medium">{fmtDateTime(s.created_at)}</p></div>
              <div><span className="text-slate-400">Responsable</span><p className="font-medium">{s.user_name}</p></div>
            </div>
            {(s.template_snapshot?.sections || []).map((sec) => {
              const singular = sec.singular_label || "Registro";
              const secAnswers = ansBySection[sec.id] || [];
              const rowIdxs = [...new Set(secAnswers.map((a) => a.row_index ?? 0))].sort((a, b) => a - b);
              return (
                <div key={sec.id}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{sec.title}</p>
                  {sec.repeatable ? (
                    <div className="space-y-3">
                      {rowIdxs.length === 0 && <p className="text-xs text-slate-400">Sin registros</p>}
                      {rowIdxs.map((ri, n) => {
                        const amapRow = Object.fromEntries(secAnswers.filter((a) => (a.row_index ?? 0) === ri).map((a) => [a.question_id, a]));
                        return (
                          <div key={ri}>
                            <p className="mb-1 text-xs font-semibold text-[#1D4ED8]">{singular} {n + 1}</p>
                            {renderRow(sec, amapRow)}
                          </div>
                        );
                      })}
                    </div>
                  ) : renderRow(sec, amap)}
                </div>
              );
            })}
            {(s.corrective_actions || []).length > 0 && (
              <div><p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Acciones correctivas</p>
                {s.corrective_actions.map((c) => <div key={c.id} className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800">{c.note} · {c.user_name}</div>)}</div>
            )}
            <Button data-testid="planilla-pdf-btn" onClick={pdf} className="w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]"><FileText className="mr-2 h-4 w-4" /> Exportar PDF</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
